import { useLazyQuery } from "@apollo/client";
import {
  Alert,
  AlertDescription,
  Box,
  Button,
  Flex,
  HStack,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "src/api";
import { BaseSimpleTransactionFields } from "src/api/fragments";
import { Query, QueryGetClientTransactionsArgs } from "src/api/generated/types";
import { useTheme } from "src/hooks/useTheme";
import { colors } from "src/theme";
import { Transaction } from "../AssetDetails/Transactions/Transaction";
import { FullServiceContext } from "./context";

const PAGE_SIZE_OPTIONS = [
  { value: 5, label: "5" },
  { value: 10, label: "10" },
  { value: 20, label: "20" },
  { value: 50, label: "50" },
];

const QUARTER_OPTIONS = [
  { value: "Q1", label: "Q1 (Jan-Mar)" },
  { value: "Q2", label: "Q2 (Apr-Jun)" },
  { value: "Q3", label: "Q3 (Jul-Sep)" },
  { value: "Q4", label: "Q4 (Oct-Dec)" },
];

function Review() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedQuarter, setSelectedQuarter] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { blueBg, medBackground, header, text, border, background } =
    useTheme();

  const { client } = useContext(FullServiceContext);

  // Determine current quarter and set based on URL
  useEffect(() => {
    const now = DateTime.now();
    const month = now.month;
    let currentQuarter = "Q1";

    if (month >= 4 && month <= 6) currentQuarter = "Q2";
    else if (month >= 7 && month <= 9) currentQuarter = "Q3";
    else if (month >= 10 && month <= 12) currentQuarter = "Q4";

    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");

    if (startDate && endDate) {
      // Try to match the dates to a quarter
      const start = DateTime.fromISO(startDate);
      const end = DateTime.fromISO(endDate);

      // Check if the dates match any quarter
      const quarter = QUARTER_OPTIONS.find((option) => {
        const { start: qStart, end: qEnd } = getQuarterDates(option.value);
        if (!qStart || !qEnd) return false;
        return (
          start.toMillis() === DateTime.fromJSDate(qStart).toMillis() &&
          end.toMillis() === DateTime.fromJSDate(qEnd).toMillis()
        );
      });

      if (quarter) {
        setSelectedQuarter(quarter.value);
      }
    } else {
      // No dates in URL, set to current quarter
      handleQuarterChange(currentQuarter);
    }
  }, []);

  const [
    fetchTransactions,
    {
      data: transactionData,
      networkStatus: transactionNetworkStatus,
      loading: transactionLoading,
      error: errorGetTransactions,
      client: transactionClient,
    },
  ] = useLazyQuery<Pick<Query, "getClientTransactions">>(
    api.clients.transactions,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-only",
    }
  );

  const getQuarterDates = (quarter: string) => {
    const now = DateTime.now();
    const year = now.year;
    let start, end;

    switch (quarter) {
      case "Q1":
        start = DateTime.local(year, 1, 1);
        end = DateTime.local(year, 3, 31);
        break;
      case "Q2":
        start = DateTime.local(year, 4, 1);
        end = DateTime.local(year, 6, 30);
        break;
      case "Q3":
        start = DateTime.local(year, 7, 1);
        end = DateTime.local(year, 9, 30);
        break;
      case "Q4":
        start = DateTime.local(year, 10, 1);
        end = DateTime.local(year, 12, 31);
        break;
      default:
        return { start: null, end: null };
    }

    return {
      start: start.toJSDate(),
      end: end.toJSDate(),
    };
  };

  const handleQuarterChange = (quarter: string) => {
    setSelectedQuarter(quarter);
    const { start, end } = getQuarterDates(quarter);
    if (start && end) {
      setSearchParams({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      });
    } else {
      setSearchParams({});
    }
  };

  const _onFetchTransactions = useCallback(async () => {
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");

    const query: QueryGetClientTransactionsArgs = {
      clientId: client?.id || "",
      limit: pageSize,
      page,
      includeSpam: false,
      labels: ["v1:save_for_later"],
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined,
    };

    const res = await fetchTransactions({
      variables: query,
      fetchPolicy: "network-only",
    });

    console.log(res);
  }, [page, pageSize, searchParams]);

  useEffect(() => {
    _onFetchTransactions();
  }, [page, pageSize, searchParams]);

  const _setPage = (newPage: number) => {
    if (newPage < 0) {
      setPage(0);
    } else {
      setPage(newPage);
    }
  };

  const transactions = (transactionData?.getClientTransactions.transactions ||
    []) as BaseSimpleTransactionFields[];
  const totalTransactions = transactionData?.getClientTransactions.total || 0;
  const isLoadingTransactions = transactionLoading;

  const totalPages = Math.ceil(totalTransactions / pageSize);

  const handlePageChange = (selectedPage: number) => {
    setPage(selectedPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(0); // Reset to first page when changing page size
  };

  return (
    <Box
      marginTop="20px"
      marginBottom={50}
      bg={background}
      borderRadius={10}
      padding={"15px 0"}
      border={`1px solid ${border}`}
    >
      <HStack padding="0 15px" mb="10px" alignItems="center">
        <Text fontFamily="Mona-Sans-Medium" fontSize="22px" color={header}>
          Review Transactions
        </Text>

        <Box
          style={{
            background: colors.primary,
            borderRadius: 50,
            height: 25,
            padding: "0 10px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "white",
            fontWeight: "bold",
          }}
        >
          {totalTransactions}
        </Box>

        <Flex flex={1} justifyContent="flex-end" gap={4}>
          <Select
            value={selectedQuarter || ""}
            onChange={(e) => handleQuarterChange(e.target.value)}
            placeholder="Quarter"
            size="sm"
            width="130px"
            color={text}
            bg={background}
            borderColor={border}
            _hover={{ borderColor: border }}
            _focus={{ borderColor: border }}
          >
            {QUARTER_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
                style={{ background: background, color: text }}
              >
                {option.label}
              </option>
            ))}
          </Select>
        </Flex>
      </HStack>

      {isLoadingTransactions && !transactions.length ? (
        <Box
          p="4rem"
          display="flex"
          justifyContent="center"
          alignItems="center"
          color={text}
        >
          <Spinner color={text} size="sm" mr="15px" /> Loading...
        </Box>
      ) : transactions.length === 0 ? (
        <Box p="40px" textAlign="center">
          <Text color={text}>
            No items to review{" "}
            <i className="fa-solid fa-check-circle" style={{ marginLeft: 5 }} />
          </Text>
        </Box>
      ) : (
        <>
          <Alert
            status="info"
            bg={blueBg}
            padding="10px"
            marginBottom="20px"
            color={text}
            w="97%"
            borderRadius="10px"
            marginLeft="auto"
            marginRight="auto"
          >
            <Box>
              <AlertDescription
                style={{
                  color: text,
                  fontSize: 14,
                  fontWeight: "medium",
                  lineHeight: 1,
                }}
              >
                Please go through and add a note on each transaction giving us a
                little more information about it for us to review.
              </AlertDescription>
            </Box>
          </Alert>

          {transactions.map((t, i) => (
            <Transaction
              timezone={client?.timezone || "UTC"}
              transaction={t}
              key={t.id}
              limitedView
              isLast={transactions.length - 1 === i}
            />
          ))}

          <HStack
            marginTop="20px"
            padding="0 15px"
            justifyContent="space-between"
            alignItems="center"
          >
            <HStack spacing={2}>
              <Button
                size="sm"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 0}
                bg={background}
                color={text}
                _hover={{ bg: medBackground }}
                _focus={{ bg: medBackground }}
              >
                <i className="fa-sharp fa-chevron-left" />
              </Button>
              <Text color={text}>
                {page + 1} of {totalPages}
              </Text>
              <Button
                size="sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages - 1}
                bg={background}
                color={text}
                _hover={{ bg: medBackground }}
                _focus={{ bg: medBackground }}
              >
                <i className="fa-sharp fa-chevron-right" />
              </Button>
            </HStack>

            <Select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
              size="sm"
              width="75px"
              color={text}
              bg={background}
              borderColor={border}
              _hover={{ borderColor: border }}
              _focus={{ borderColor: border }}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  style={{ background: background, color: text }}
                >
                  {option.label}
                </option>
              ))}
            </Select>
          </HStack>
        </>
      )}
    </Box>
  );
}

export default Review;
